import { createEntityAdapter, createSelector, createSlice, EntityState, PayloadAction } from '@reduxjs/toolkit';
import { UnitOfMeasureType } from '../../api/models/api-shared.enums';
import { UnitOfMeasureOrderQuantity } from '../../api/models/api-shared.models';
import { UpdateCustomProductRequest } from '../../api/models/customer-product.models';
import { ProductDetailTransfer, USDAProductStatusTypeData } from '../../api/models/product-detail.models';
import { ProductListProduct, SearchProductListCatalogFieldRequest } from '../../api/models/product-list-search.models';
import { normalizeProductKey, updateProductCustomAttributesFromPayload } from '../../helpers/general/product';
import { getSearchProductListCatalogFieldRequestDefault } from '../../helpers/general/product-list';
import { RootState } from '../store';

export const getItemOrderEntryRowId = (product: ProductListProduct): string => {
  let id = normalizeProductKey(product.ProductKey);

  for (const uom of product.UnitOfMeasureOrderQuantities) {
    id += `_${uom.UnitOfMeasure}`;
  }

  return id;
};

export const getMatchingProductRows = (
  state: ItemOrderEntryState,
  productKey: string,
  uomType: number
): ProductListProduct[] => {
  const normalizedKey = normalizeProductKey(productKey);

  return Object.values(state.products.entities).filter(
    (product): product is ProductListProduct =>
      !!product &&
      normalizeProductKey(product.ProductKey) === normalizedKey &&
      product.UnitOfMeasureOrderQuantities.some((u) => u.UnitOfMeasure === uomType)
  );
};

// Adapters
const productsAdapter = createEntityAdapter<ProductListProduct>({
  selectId: getItemOrderEntryRowId,
});

// State
export interface ItemOrderEntryState {
  products: EntityState<ProductListProduct>;
  pageIsLoading: boolean;
  searchProductListCatalogFieldRequest: SearchProductListCatalogFieldRequest;
  searchingProductList: boolean;
  loadingDefaultSearchType: boolean;
  selectableProducts: ProductListProduct[];
  noSelectableProducts: boolean;
  focus: { rowIndex: number; elementIndex?: number } | undefined;
  prevQuery: string | undefined;
}

const initialState: ItemOrderEntryState = {
  products: productsAdapter.getInitialState(),
  pageIsLoading: false,
  searchProductListCatalogFieldRequest: getSearchProductListCatalogFieldRequestDefault(),
  searchingProductList: false,
  loadingDefaultSearchType: false,
  selectableProducts: [],
  noSelectableProducts: false,
  focus: undefined,
  prevQuery: undefined,
};

// Reducers
export const itemOrderEntrySlice = createSlice({
  name: 'itemOrderEntry',
  initialState: initialState,
  reducers: {
    resetState: () => {
      return initialState;
    },
    addProduct: (state: ItemOrderEntryState, action: PayloadAction<ProductListProduct>) => {
      const incoming = action.payload;

      const alreadyExists = Object.values(state.products.entities).some(
        (product): product is ProductListProduct =>
          !!product &&
          normalizeProductKey(product.ProductKey) === normalizeProductKey(incoming.ProductKey) &&
          incoming.UnitOfMeasureOrderQuantities.some((incomingUom) =>
            product.UnitOfMeasureOrderQuantities.some(
              (existingUom) => existingUom.UnitOfMeasure === incomingUom.UnitOfMeasure
            )
          )
      );

      if (alreadyExists) {
        return;
      }

      productsAdapter.upsertOne(state.products, incoming);
    },
    setProducts: (state: ItemOrderEntryState, action: PayloadAction<ProductListProduct[]>) => {
      productsAdapter.setAll(state.products, action.payload);
    },
    setSearchProductListCatalogFieldRequest: (
      state: ItemOrderEntryState,
      action: PayloadAction<SearchProductListCatalogFieldRequest>
    ) => {
      state.prevQuery = state.searchProductListCatalogFieldRequest.Query;
      state.searchProductListCatalogFieldRequest = action.payload;
    },
    setPageIsLoading: (state: ItemOrderEntryState, action: PayloadAction<boolean>) => {
      state.pageIsLoading = action.payload;
    },
    setSelectableProducts: (state: ItemOrderEntryState, action: PayloadAction<ProductListProduct[]>) => {
      state.selectableProducts = action.payload;
    },
    setNoSelectableProducts: (state: ItemOrderEntryState, action: PayloadAction<boolean>) => {
      state.noSelectableProducts = action.payload;
    },
    clearRequestQuery: (state: ItemOrderEntryState) => {
      state.prevQuery = state.searchProductListCatalogFieldRequest.Query;
      state.searchProductListCatalogFieldRequest.Query = '';
    },
    setSearchingProductList: (state: ItemOrderEntryState, action: PayloadAction<boolean>) => {
      state.searchingProductList = action.payload;
    },
    setLoadingDefaultSearchType: (state: ItemOrderEntryState, action: PayloadAction<boolean>) => {
      state.loadingDefaultSearchType = action.payload;
    },
    resetFocus: (state: ItemOrderEntryState) => {
      state.focus = undefined;
    },
    setFocus: (
      state: ItemOrderEntryState,
      action: PayloadAction<{
        productKey?: string;
        uomType?: UnitOfMeasureType;
      }>
    ) => {
      const normalizedKey = normalizeProductKey(action.payload.productKey);
      const matchingProducts = Object.entries(state.products.entities).filter(
        ([_, product]) => !!product && normalizeProductKey(product.ProductKey) === normalizedKey
      );
      let rowId: string | undefined;
      let elementIndex = 0;

      for (const [id, product] of matchingProducts) {
        if (!product) {
          continue;
        }
        const uomIndex = product.UnitOfMeasureOrderQuantities.findIndex(
          (u) => u.UnitOfMeasure === action.payload.uomType
        );

        if (uomIndex >= 0) {
          rowId = id;
          elementIndex = uomIndex;
          break;
        }
      }

      if (!rowId) {
        state.focus = undefined;
        return;
      }
      const rowIndex = Math.max(state.products.ids.indexOf(rowId), 0);

      state.focus = {
        rowIndex,
        elementIndex,
      };
    },
    updateProduct: (state: ItemOrderEntryState, action: PayloadAction<ProductDetailTransfer>) => {
      const products = getMatchingProductRows(state, action.payload.productKey, action.payload.uom);

      for (const product of products) {
        product.UnitOfMeasureOrderQuantities.forEach((uom: UnitOfMeasureOrderQuantity) => {
          if (uom.UnitOfMeasure === action.payload.uom) {
            uom.ExtendedPrice = action.payload.extendedPrice;
            uom.Quantity = action.payload.quantity;
            uom.ShowOrderQuantityAlert = action.payload.showOrderQuantityAlert;
          }
        });
      }
    },
    updateProductCustomAttributes: (state: ItemOrderEntryState, action: PayloadAction<UpdateCustomProductRequest>) => {
      const normalizedKey = normalizeProductKey(action.payload.ProductKey);

      Object.values(state.products.entities)
        .filter(
          (product): product is ProductListProduct =>
            !!product && normalizeProductKey(product.ProductKey) === normalizedKey
        )
        .forEach((product) => {
          updateProductCustomAttributesFromPayload(product, action.payload);
        });
    },
    setUsdaProductStatusType: (state: ItemOrderEntryState, action: PayloadAction<USDAProductStatusTypeData[]>) => {
      action.payload.forEach((statusResult: USDAProductStatusTypeData) => {
        if (!statusResult.ProductKey) {
          return;
        }

        const normalizedKey = normalizeProductKey(statusResult.ProductKey);

        Object.values(state.products.entities)
          .filter(
            (product): product is ProductListProduct =>
              !!product && normalizeProductKey(product.ProductKey) === normalizedKey
          )
          .forEach((product) => {
            product.USDAProductStatusType = statusResult.USDAProductStatusType;
          });
      });
    },
  },
});

// Adapter Selectors
export const {
  selectAll: selectAllItemOrderEntryProducts,
  selectById: selectItemOrderEntryProductsByKey,
  selectIds: selectAllItemOrderEntryProductKeys,
} = productsAdapter.getSelectors<RootState>((state: RootState) => state.itemOrderEntry.products);

const selectOrderEntryProductsLoaded = createSelector(
  (state: RootState) => selectAllItemOrderEntryProducts(state),
  (products) => products.length > 0
);
export const selectOrderEntryProductsWithPrice = createSelector(
  (state: RootState) => selectAllItemOrderEntryProducts(state),
  (products) =>
    products.flatMap((p) =>
      p.UnitOfMeasureOrderQuantities.map((uom) => ({ product: p, uom })).filter((data) => data.uom.Price)
    ).length > 0
);

export const isItemOrderEntryProductsPagePricesLoaded = createSelector(
  [selectOrderEntryProductsWithPrice, selectOrderEntryProductsLoaded],
  (didOrderEntryPriceLoad, didOrderEntryProductsLoad): boolean => {
    return !didOrderEntryPriceLoad && didOrderEntryProductsLoad;
  }
);

export const selectItemOrderEntryUsdaProductKeys = createSelector(selectAllItemOrderEntryProducts, (products) =>
  products.filter((p) => p.IsUSDAProduct).map((p) => p.ProductKey ?? '')
);
